import api from './api'

/**
 * Modelo del constructor de páginas. La estructura es la clásica de este tipo
 * de editores: la página son secciones, cada sección tiene columnas y cada
 * columna apila widgets. Todo vive en el campo `nodes` (jsonb) de la página.
 */

export type WidgetTypeType =
  | 'heading'
  | 'text'
  | 'markdown'
  | 'image'
  | 'gallery'
  | 'button'
  | 'video'
  | 'form'
  | 'features'
  | 'testimonials'
  | 'card'
  | 'alert'
  | 'accordion'
  | 'tabs'
  | 'list'
  | 'chips'
  | 'profile'
  | 'table'
  | 'navbar'
  | 'breadcrumbs'
  | 'modal'
  | 'drawer'
  | 'hero'
  | 'cta'
  | 'stats'
  | 'pricing'
  | 'steps'
  | 'team'
  | 'logos'
  | 'quote'
  | 'contact'
  | 'social'
  | 'map'
  | 'embed'
  | 'code'
  | 'progress'
  | 'buttons'
  | 'footer'
  | 'icon'
  | 'divider'
  | 'spacer'
  | 'html'

export interface WidgetPropsType {
  // heading / text
  text?: string
  level?: 1 | 2 | 3 | 4
  size?: 'sm' | 'md' | 'lg'
  align?: 'left' | 'center' | 'right'
  tone?: 'default' | 'muted' | 'primary'
  // image
  src?: string
  alt?: string
  rounded?: boolean
  maxWidth?: number
  // gallery
  images?: { src: string; alt?: string; caption?: string }[]
  columns?: 2 | 3 | 4
  // button
  label?: string
  href?: string
  color?: 'primary' | 'secondary' | 'success' | 'danger' | 'warning' | 'info'
  variant?: 'contained' | 'outlined' | 'soft' | 'text'
  buttonSize?: 'sm' | 'md' | 'lg'
  newTab?: boolean
  // video
  url?: string
  // form
  formSlug?: string
  formTitle?: string
  showFormTitle?: boolean
  // features
  items?: { icon?: string; title?: string; text?: string }[]
  // testimonials (comentarios de clientes)
  quotes?: { text?: string; author?: string; role?: string; avatar?: string; rating?: number }[]
  // navbar / breadcrumbs / drawer / lista de enlaces
  links?: { label?: string; href?: string; icon?: string }[]
  brand?: string
  sticky?: boolean
  // alert
  outlined?: boolean
  icon?: string
  title?: string
  // chips
  tags?: string[]
  // tabla
  headers?: string[]
  rows?: string[][]
  striped?: boolean
  // modal / drawer
  position?: 'left' | 'right' | 'top' | 'bottom'
  panelSize?: 'sm' | 'md' | 'lg' | 'xl'
  // tabs
  tabsVariant?: 'default' | 'pills' | 'underline' | 'buttons'
  // hero / cta: segundo botón
  secondaryLabel?: string
  secondaryHref?: string
  // stats / progress / pricing: los valores de cada bloque
  values?: { label?: string; value?: string; suffix?: string; icon?: string; highlight?: boolean; features?: string[] }[]
  // mapa e incrustados
  embedHeight?: number
  // spacer
  height?: 'sm' | 'md' | 'lg' | 'xl'
  // html
  code?: string
}

export interface WidgetType {
  id: string
  type: WidgetTypeType
  props: WidgetPropsType
}

export interface ColumnType {
  id: string
  /** Ancho en una rejilla de 12. */
  span: number
  widgets: WidgetType[]
}

export type SectionBackgroundType = 'none' | 'surface' | 'muted' | 'primary' | 'dark'

export interface SectionStyleType {
  background?: SectionBackgroundType
  /** Imagen de fondo opcional (URL o data URI). */
  backgroundImage?: string
  paddingY?: 'none' | 'sm' | 'md' | 'lg' | 'xl'
  width?: 'boxed' | 'wide' | 'full'
  gap?: 'sm' | 'md' | 'lg'
  /** En móvil las columnas siempre se apilan; esto invierte ese orden. */
  reverseOnMobile?: boolean
}

export interface SectionType {
  id: string
  columns: ColumnType[]
  style: SectionStyleType
}

export interface PageSettingsType {
  title?: string
  description?: string
  /** Muestra la barra de navegación y el pie del sitio público. */
  showChrome?: boolean
  maxWidth?: 'md' | 'lg' | 'xl' | 'full'
}

export interface PageContentType {
  sections: SectionType[]
  settings: PageSettingsType
}

export interface PageType {
  id: string
  companyId?: string
  name: string
  slug: string
  status: 'draft' | 'published'
  nodes: PageContentType | Record<string, unknown> | null
  createdAt?: string
  updatedAt?: string
}

/** Identificadores cortos y únicos dentro de la página; no salen de aquí. */
const uid = (prefix: string) => `${prefix}_${Math.random().toString(36).slice(2, 9)}`

export interface WidgetDefinitionType {
  type: WidgetTypeType
  label: string
  icon: string
  /** Agrupación en la paleta. */
  group: 'Bloques' | 'Contenido' | 'Medios' | 'Interacción' | 'Navegación' | 'Datos' | 'Estructura'
  defaults: WidgetPropsType
}

/** Catálogo de la paleta: de aquí salen los widgets que se arrastran al lienzo. */
export const WIDGET_CATALOG: WidgetDefinitionType[] = [
  {
    type: 'heading',
    label: 'Título',
    icon: 'material-symbols-light:title',
    group: 'Contenido',
    defaults: { text: 'Un título que enganche', level: 2, align: 'left', tone: 'default' }
  },
  {
    type: 'text',
    label: 'Texto',
    icon: 'material-symbols-light:notes',
    group: 'Contenido',
    defaults: { text: 'Escribe aquí el contenido de tu página.', align: 'left', size: 'md', tone: 'muted' }
  },
  {
    type: 'features',
    label: 'Lista de ventajas',
    icon: 'material-symbols-light:checklist',
    group: 'Contenido',
    defaults: {
      columns: 3,
      items: [
        { icon: 'material-symbols-light:bolt-outline', title: 'Rápido', text: 'Explica aquí la ventaja.' },
        { icon: 'material-symbols-light:verified-outline', title: 'Confiable', text: 'Explica aquí la ventaja.' },
        { icon: 'material-symbols-light:support-agent', title: 'Acompañado', text: 'Explica aquí la ventaja.' }
      ]
    }
  },
  {
    type: 'testimonials',
    label: 'Comentarios',
    icon: 'material-symbols-light:format-quote',
    group: 'Contenido',
    defaults: {
      columns: 3,
      quotes: [
        { text: 'Cuenta aquí lo que dijo tu cliente sobre el trabajo.', author: 'Nombre del cliente', role: 'Cargo o empresa', rating: 5 },
        { text: 'Un segundo comentario refuerza la confianza de quien te lee.', author: 'Otro cliente', role: 'Cargo o empresa', rating: 5 }
      ]
    }
  },
  {
    type: 'image',
    label: 'Imagen',
    icon: 'material-symbols-light:image-outline',
    group: 'Medios',
    defaults: { src: '', alt: '', rounded: true, align: 'center' }
  },
  {
    type: 'gallery',
    label: 'Galería',
    icon: 'material-symbols-light:gallery-thumbnail-outline',
    group: 'Medios',
    defaults: { images: [], columns: 3, rounded: true }
  },
  {
    type: 'video',
    label: 'Video',
    icon: 'material-symbols-light:play-circle-outline',
    group: 'Medios',
    defaults: { url: '' }
  },
  {
    type: 'button',
    label: 'Botón',
    icon: 'material-symbols-light:buttons-alt-outline',
    group: 'Interacción',
    defaults: { label: 'Quiero saber más', href: '#', color: 'primary', variant: 'contained', buttonSize: 'md', align: 'left' }
  },
  {
    type: 'form',
    label: 'Formulario',
    icon: 'material-symbols-light:list-alt-outline',
    group: 'Interacción',
    defaults: { formSlug: '', showFormTitle: true }
  },
  {
    type: 'markdown',
    label: 'Markdown',
    icon: 'material-symbols-light:markdown-outline',
    group: 'Contenido',
    defaults: { text: '## Un subtítulo\n\nTexto con **negrita**, *cursiva* y [enlaces](https://florezjaramillo.com).' }
  },
  {
    type: 'card',
    label: 'Tarjeta',
    icon: 'material-symbols-light:crop-square',
    group: 'Contenido',
    defaults: { title: 'Título de la tarjeta', text: 'Describe aquí el contenido.', label: '', href: '', color: 'primary' }
  },
  {
    type: 'alert',
    label: 'Aviso',
    icon: 'material-symbols-light:info-outline',
    group: 'Contenido',
    defaults: { color: 'info', title: 'Ten en cuenta', text: 'Un mensaje destacado para quien visita la página.', outlined: false }
  },
  {
    type: 'accordion',
    label: 'Preguntas frecuentes',
    icon: 'material-symbols-light:expand-circle-down-outline',
    group: 'Contenido',
    defaults: {
      items: [
        { title: '¿Cuánto tarda un proyecto?', text: 'Depende del alcance; una web sencilla toma entre 2 y 4 semanas.' },
        { title: '¿Cómo se paga?', text: '50% al inicio y 50% contra entrega.' }
      ]
    }
  },
  {
    type: 'list',
    label: 'Lista',
    icon: 'material-symbols-light:list',
    group: 'Contenido',
    defaults: {
      items: [
        { icon: 'material-symbols-light:check-circle-outline', title: 'Primer punto', text: 'Detalle del punto.' },
        { icon: 'material-symbols-light:check-circle-outline', title: 'Segundo punto', text: 'Detalle del punto.' }
      ]
    }
  },
  {
    type: 'chips',
    label: 'Etiquetas',
    icon: 'material-symbols-light:sell-outline',
    group: 'Contenido',
    defaults: { tags: ['SolidJS', 'TypeScript', 'PostgreSQL'], color: 'primary', outlined: false }
  },
  {
    type: 'profile',
    label: 'Persona',
    icon: 'material-symbols-light:account-circle-outline',
    group: 'Contenido',
    defaults: { title: 'Nombre y apellido', text: 'Cargo o especialidad', src: '', align: 'center' }
  },
  {
    type: 'tabs',
    label: 'Pestañas',
    icon: 'material-symbols-light:tab-outline',
    group: 'Navegación',
    defaults: {
      tabsVariant: 'underline',
      items: [
        { title: 'Web', text: 'Qué ofreces en esta pestaña.' },
        { title: 'Móvil', text: 'Contenido de la segunda pestaña.' }
      ]
    }
  },
  {
    type: 'navbar',
    label: 'Barra de navegación',
    icon: 'material-symbols-light:menu',
    group: 'Navegación',
    defaults: {
      brand: 'Flórez Jaramillo',
      sticky: false,
      links: [
        { label: 'Inicio', href: '/' },
        { label: 'Servicios', href: '#servicios' },
        { label: 'Contacto', href: '#contacto' }
      ],
      label: 'Cotizar',
      href: '#contacto',
      color: 'primary'
    }
  },
  {
    type: 'breadcrumbs',
    label: 'Migas de pan',
    icon: 'material-symbols-light:chevron-right',
    group: 'Navegación',
    defaults: {
      links: [
        { label: 'Inicio', href: '/' },
        { label: 'Servicios', href: '' }
      ]
    }
  },
  {
    type: 'modal',
    label: 'Ventana modal',
    icon: 'material-symbols-light:open-in-full',
    group: 'Interacción',
    defaults: { label: 'Ver detalle', title: 'Título de la ventana', text: 'Contenido que se muestra al abrir.', color: 'primary', variant: 'contained', panelSize: 'md', align: 'left' }
  },
  {
    type: 'drawer',
    label: 'Panel lateral',
    icon: 'material-symbols-light:dock-to-right-outline',
    group: 'Interacción',
    defaults: {
      label: 'Abrir panel',
      title: 'Panel lateral',
      text: 'Contenido del panel.',
      position: 'right',
      panelSize: 'sm',
      color: 'primary',
      variant: 'soft',
      align: 'left',
      links: []
    }
  },
  {
    type: 'table',
    label: 'Tabla',
    icon: 'material-symbols-light:table-outline',
    group: 'Datos',
    defaults: {
      headers: ['Plan', 'Incluye', 'Precio'],
      rows: [
        ['Básico', 'Sitio de 5 páginas', '2.500.000 COP'],
        ['Pro', 'Sitio + panel admin', '6.000.000 COP']
      ],
      striped: true
    }
  },
  {
    type: 'hero',
    label: 'Portada',
    icon: 'material-symbols-light:star-shine-outline',
    group: 'Bloques',
    defaults: {
      title: 'El titular que convence',
      text: 'Una frase corta que explique a qué te dedicas y por qué elegirte.',
      label: 'Empezar',
      href: '#contacto',
      secondaryLabel: 'Ver trabajos',
      secondaryHref: '#trabajos',
      src: '',
      align: 'left',
      color: 'primary'
    }
  },
  {
    type: 'cta',
    label: 'Llamada a la acción',
    icon: 'material-symbols-light:campaign-outline',
    group: 'Bloques',
    defaults: { title: '¿Empezamos?', text: 'Cuéntanos tu idea y te respondemos hoy mismo.', label: 'Hablemos', href: '#contacto', color: 'primary', align: 'center' }
  },
  {
    type: 'stats',
    label: 'Cifras',
    icon: 'material-symbols-light:trending-up',
    group: 'Bloques',
    defaults: {
      columns: 3,
      values: [
        { value: '+120', label: 'Proyectos entregados' },
        { value: '8', label: 'Años de experiencia' },
        { value: '98%', label: 'Clientes que repiten' }
      ]
    }
  },
  {
    type: 'pricing',
    label: 'Planes',
    icon: 'material-symbols-light:payments-outline',
    group: 'Bloques',
    defaults: {
      columns: 3,
      label: 'Elegir plan',
      values: [
        { label: 'Básico', value: '2.500.000 COP', suffix: 'pago único', features: ['Sitio de 5 páginas', 'Diseño a medida', 'Formulario de contacto'] },
        { label: 'Pro', value: '6.000.000 COP', suffix: 'pago único', highlight: true, features: ['Todo lo del Básico', 'Panel administrativo', 'Integraciones'] },
        { label: 'A medida', value: 'Hablemos', suffix: 'según alcance', features: ['Producto propio', 'Equipo dedicado', 'Soporte continuo'] }
      ]
    }
  },
  {
    type: 'steps',
    label: 'Proceso',
    icon: 'material-symbols-light:footprint',
    group: 'Bloques',
    defaults: {
      items: [
        { title: 'Nos cuentas', text: 'Entendemos qué necesitas y para cuándo.' },
        { title: 'Proponemos', text: 'Alcance, tiempos y precio por escrito.' },
        { title: 'Construimos', text: 'Entregas revisables cada semana.' }
      ]
    }
  },
  {
    type: 'team',
    label: 'Equipo',
    icon: 'material-symbols-light:groups-outline',
    group: 'Bloques',
    defaults: {
      columns: 3,
      quotes: [
        { author: 'Nombre y apellido', role: 'Dirección' },
        { author: 'Nombre y apellido', role: 'Desarrollo' },
        { author: 'Nombre y apellido', role: 'Diseño' }
      ]
    }
  },
  {
    type: 'logos',
    label: 'Clientes',
    icon: 'material-symbols-light:handshake-outline',
    group: 'Bloques',
    defaults: { title: 'Confían en nosotros', images: [] }
  },
  {
    type: 'quote',
    label: 'Cita destacada',
    icon: 'material-symbols-light:format-quote-outline',
    group: 'Contenido',
    defaults: { text: 'Una frase que resuma tu propuesta de valor.', title: 'Quién lo dice', align: 'center' }
  },
  {
    type: 'contact',
    label: 'Datos de contacto',
    icon: 'material-symbols-light:contact-support-outline',
    group: 'Bloques',
    defaults: {
      items: [
        { icon: 'material-symbols-light:call-outline', title: 'Teléfono', text: '+57 300 000 0000' },
        { icon: 'material-symbols-light:mail-outline', title: 'Correo', text: 'hola@tudominio.com' },
        { icon: 'material-symbols-light:location-on-outline', title: 'Dirección', text: 'Medellín, Colombia' }
      ],
      columns: 3
    }
  },
  {
    type: 'social',
    label: 'Redes sociales',
    icon: 'material-symbols-light:share-outline',
    group: 'Navegación',
    defaults: {
      align: 'left',
      links: [
        { label: 'Instagram', href: 'https://instagram.com/', icon: 'mdi:instagram' },
        { label: 'LinkedIn', href: 'https://linkedin.com/', icon: 'mdi:linkedin' },
        { label: 'WhatsApp', href: 'https://wa.me/57300000000', icon: 'mdi:whatsapp' }
      ]
    }
  },
  {
    type: 'map',
    label: 'Mapa',
    icon: 'material-symbols-light:map-outline',
    group: 'Medios',
    defaults: { text: 'Medellín, Colombia', embedHeight: 320 }
  },
  {
    type: 'embed',
    label: 'Incrustado',
    icon: 'material-symbols-light:iframe-outline',
    group: 'Medios',
    defaults: { url: '', embedHeight: 420 }
  },
  {
    type: 'code',
    label: 'Bloque de código',
    icon: 'material-symbols-light:terminal',
    group: 'Contenido',
    defaults: { code: 'bun install\nbun run dev', title: 'Terminal' }
  },
  {
    type: 'progress',
    label: 'Barras de nivel',
    icon: 'material-symbols-light:bar-chart',
    group: 'Datos',
    defaults: {
      color: 'primary',
      values: [
        { label: 'SolidJS', value: '90' },
        { label: 'TypeScript', value: '85' },
        { label: 'PostgreSQL', value: '75' }
      ]
    }
  },
  {
    type: 'buttons',
    label: 'Grupo de botones',
    icon: 'material-symbols-light:tab-group-outline',
    group: 'Interacción',
    defaults: {
      align: 'left',
      color: 'primary',
      links: [
        { label: 'Cotizar', href: '#contacto' },
        { label: 'Ver trabajos', href: '#trabajos' }
      ]
    }
  },
  {
    type: 'icon',
    label: 'Icono',
    icon: 'material-symbols-light:emoji-symbols-outline',
    group: 'Contenido',
    defaults: { icon: 'material-symbols-light:rocket-launch-outline', align: 'center', color: 'primary', maxWidth: 48 }
  },
  {
    type: 'footer',
    label: 'Pie de página',
    icon: 'material-symbols-light:bottom-panel-close-outline',
    group: 'Navegación',
    defaults: {
      brand: 'Flórez Jaramillo',
      text: '© 2026 Todos los derechos reservados.',
      links: [
        { label: 'Inicio', href: '/' },
        { label: 'Servicios', href: '#servicios' },
        { label: 'Contacto', href: '#contacto' }
      ]
    }
  },
  {
    type: 'divider',
    label: 'Separador',
    icon: 'material-symbols-light:horizontal-rule',
    group: 'Estructura',
    defaults: {}
  },
  {
    type: 'spacer',
    label: 'Espacio',
    icon: 'material-symbols-light:space-bar',
    group: 'Estructura',
    defaults: { height: 'md' }
  },
  {
    type: 'html',
    label: 'HTML',
    icon: 'material-symbols-light:code',
    group: 'Estructura',
    defaults: { code: '<p>Tu HTML aquí</p>' }
  }
]

export const widgetDefinition = (type: WidgetTypeType) => WIDGET_CATALOG.find(w => w.type === type)

export const createWidget = (type: WidgetTypeType): WidgetType => ({
  id: uid('w'),
  type,
  // Copia profunda de los valores por defecto: si no, dos widgets del mismo
  // tipo compartirían el mismo array de imágenes o de ventajas.
  props: structuredClone(widgetDefinition(type)?.defaults ?? {})
})

export const createColumn = (span = 12): ColumnType => ({ id: uid('c'), span, widgets: [] })

/** Distribuciones de columnas que ofrece el botón de "nueva sección". */
export const SECTION_LAYOUTS: { key: string; label: string; spans: number[] }[] = [
  { key: '12', label: '1 columna', spans: [12] },
  { key: '6-6', label: '2 columnas', spans: [6, 6] },
  { key: '4-4-4', label: '3 columnas', spans: [4, 4, 4] },
  { key: '3-3-3-3', label: '4 columnas', spans: [3, 3, 3, 3] },
  { key: '8-4', label: '2/3 + 1/3', spans: [8, 4] },
  { key: '4-8', label: '1/3 + 2/3', spans: [4, 8] }
]

export const createSection = (spans: number[] = [12]): SectionType => ({
  id: uid('s'),
  columns: spans.map(createColumn),
  style: { background: 'none', paddingY: 'lg', width: 'boxed', gap: 'md' }
})

export const emptyContent = (): PageContentType => ({
  sections: [createSection([12])],
  settings: { showChrome: true, maxWidth: 'lg' }
})

/**
 * Las páginas antiguas guardaban cualquier cosa en `nodes` (incluso `{}` o el
 * JSON crudo que se editaba a mano), así que se normaliza antes de usarlo.
 */
export function normalizeContent(nodes: unknown): PageContentType {
  // Hubo páginas guardadas como cadena JSON dentro del jsonb; se aceptan igual.
  let source = nodes
  if (typeof source === 'string') {
    try {
      source = JSON.parse(source)
    } catch {
      source = {}
    }
  }

  const raw = (source ?? {}) as Partial<PageContentType>
  const sections = Array.isArray(raw.sections) ? raw.sections : []

  return {
    settings: { showChrome: true, maxWidth: 'lg', ...(raw.settings ?? {}) },
    sections: sections.map(section => ({
      id: section.id || uid('s'),
      style: { background: 'none', paddingY: 'lg', width: 'boxed', gap: 'md', ...(section.style ?? {}) },
      columns: (Array.isArray(section.columns) ? section.columns : []).map(column => ({
        id: column.id || uid('c'),
        span: column.span || 12,
        widgets: (Array.isArray(column.widgets) ? column.widgets : []).map(widget => ({
          id: widget.id || uid('w'),
          type: widget.type,
          props: widget.props ?? {}
        }))
      }))
    }))
  }
}

/** Cuenta los widgets de la página: se usa para avisar de páginas vacías. */
export const countWidgets = (content: PageContentType) => content.sections.reduce((total, section) => total + section.columns.reduce((acc, column) => acc + column.widgets.length, 0), 0)

/**
 * Convierte la URL de YouTube o Vimeo en la de incrustar. Si no se reconoce, se
 * devuelve tal cual para no romper enlaces de otros proveedores.
 */
export function embedUrl(url: string): string {
  const value = (url ?? '').trim()
  if (!value) return ''

  const youtube = value.match(/(?:youtube\.com\/(?:watch\?v=|embed\/)|youtu\.be\/)([\w-]{6,})/)
  if (youtube) return `https://www.youtube.com/embed/${youtube[1]}`

  const vimeo = value.match(/vimeo\.com\/(?:video\/)?(\d+)/)
  if (vimeo) return `https://player.vimeo.com/video/${vimeo[1]}`

  return value
}

export const pageService = {
  async list(params?: { page?: number; limit?: number }) {
    const { data } = await api.get('/pages', { params })
    return (data?.data?.items ?? []) as PageType[]
  },

  async get(id: string) {
    const { data } = await api.get(`/pages/${id}`)
    return data.data as PageType
  },

  async create(payload: { name: string; slug: string }) {
    const { data } = await api.post('/pages', payload)
    return data.data as PageType
  },

  async update(id: string, payload: Partial<Pick<PageType, 'name' | 'slug' | 'status'>> & { nodes?: PageContentType }) {
    const { data } = await api.put(`/pages/${id}`, payload)
    return data.data as PageType
  },

  async publish(id: string) {
    const { data } = await api.post(`/pages/${id}/publish`, {})
    return data.data as PageType
  },

  async delete(id: string) {
    const { data } = await api.delete(`/pages/${id}`)
    return data.data as boolean
  },

  /** Elimina varias páginas de una vez. Devuelve cuántas se borraron. */
  async bulkDelete(ids: string[]) {
    const { data } = await api.post('/pages/bulk-delete', { ids })
    return data.data as { deleted: number }
  },

  /** Página publicada, sin sesión: es lo que consume la ruta pública /p/:slug. */
  async getPublic(slug: string) {
    const { data } = await api.get(`/public/pages/${slug}`)
    return data.data as PageType
  }
}
